import Vue from 'vue'
import Vuex from 'vuex'
import Axios from 'axios'
import uiModule from './ui'
import authModule from './auth'
import apiModule from './api'
import limitsModule from './limits'
import partnersForSelect from './partnersForSelect'
import operatorsModule from './operators'
import partnerGroups from './partnerGroups'
import tableGameTypeGroups from './tableGameTypeGroups'
import gameTypes from './gameTypes'
import helpFolders from './helpFolders'
import sourcesModule from './sources'
import currenciesForSelect from './currenciesForSelect'
import operatorsForSelect from './operatorsForSelect'
import softSwissGamesForSelect from './softSwissGamesForSelect'
import providerGames from './providerGames'
import tablesForSelect from './tablesForSelect'
import engineOperators from './engineOperators'
import tableStatuses from './tableStatuses'

Vue.use(Vuex)

function getInitialState() {
  return {
    status: null,
    token: localStorage.getItem('token') || null,
    user: {
      username: localStorage.getItem('username') || null
    },
    roles: [],
    tables: [],
    partners: [],
    operators: [],
    currentUser: null,
    currentUserPromise: null,
    dashboards: [],
    currencyCodes: [],
    showDebugTools: false,
    currentUserLoaded: false,
    adminChatMessages: []
  }
}

function isDebugEnabled(state) {
  if (process.env.NODE_ENV === 'development') {
    return true
  }

  return !!(
    state.currentUser &&
    process.env.VUE_APP_DEBUG_USERNAME &&
    state.currentUser.username === process.env.VUE_APP_DEBUG_USERNAME
  )
}

export default new Vuex.Store({
  state: getInitialState(),
  mutations: {
    AUTH_REQUEST(state) {
      state.status = 'loading'
    },
    AUTH_SUCCESS(state, { token, username }) {
      state.status = 'success'
      state.token = token
      state.username = username
    },
    AUTH_ERROR(state) {
      state.status = 'error'
    },
    CURRENT_USER_LOADED(state, currentUser) {
      state.currentUser = currentUser
    },
    TOGGLE_DEBUG_TOOLS(state) {
      state.showDebugTools = !state.showDebugTools
    },
    PASSWORD_RESET_ATTEMPT_FAILED(state) {
      state.authRefreshAttemptFailed = true
    },
    PASSWORD_RESET_ATTEMPT_SUCCEED(state) {
      state.authRefreshAttemptSucceed = true
    },
    ADD_ADMIN_CHAT_MESSAGE(state, message) {
      state.adminChatMessages.push(message)
    }
  },
  actions: {
    addAdminChatMessage({ commit }, message) {
      commit('ADD_ADMIN_CHAT_MESSAGE', message)
    },
    login({ commit, dispatch }, user) {
      return new Promise((resolve, reject) => {
        commit('AUTH_REQUEST')

        Axios({
          url: '/api/v1/token/get',
          data: user,
          method: 'POST'
        })
          .then(resp => {
            const token = resp.data.token
            const refreshToken = resp.data.refreshToken

            localStorage.setItem('token', token)
            localStorage.setItem('refreshToken', refreshToken)
            localStorage.setItem('username', user.username)

            Axios.defaults.headers.common['Authorization'] = 'Bearer ' + token

            commit('AUTH_SUCCESS', { token, user })

            dispatch('loadApplicationData')

            resolve(resp)
          })
          .catch(err => {
            commit('AUTH_ERROR')
            reject(err)
          })
      })
    },
    logout() {
      this.replaceState(getInitialState())

      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('username')

      delete Axios.defaults.headers.common['Authorization']
    },
    loadApplicationData({ dispatch }) {
      if (this.state.token === null) {
        return
      }

      dispatch('loadCurrentUser')
    },
    loadCurrentUser({ commit }) {
      if (this.state.currentUser !== null) {
        return
      }

      if (this.state.currentUserPromise !== null) {
        return this.state.currentUserPromise
      }

      this.state.currentUserPromise = Axios({
        url: '/api/v1/users/me',
        method: 'GET'
      })
        .then(resp => {
          commit('CURRENT_USER_LOADED', resp.data)
          this.state.currentUserLoaded = true
        })
        .catch(err => {
          console.log(err)
        })
        .finally(() => {
          this.state.currentUserPromise = null
        })

      return this.state.currentUserPromise
    },
    toggleDebugTools({ commit }) {
      commit('TOGGLE_DEBUG_TOOLS')
    }
  },
  getters: {
    adminChatMessages: state => state.adminChatMessages,
    isLoggedIn: state => !!state.token,
    username: state => state.user.username,
    getCurrentUser: state => state.currentUser,
    isDebugEnabled: state => isDebugEnabled(state),
    isShowDebugTools: state => state.showDebugTools,
    getRoles: state => state.roles,
    getTables: state => state.tables,
    currentUser: state => state.currentUser,
    currentUserLoaded: state => state.currentUserLoaded
  },
  modules: {
    ui: uiModule,
    auth: authModule,
    api: apiModule,
    limits: limitsModule,
    partnersForSelect,
    tablesForSelect,
    operatorsModule,
    partnerGroups,
    tableGameTypeGroups,
    gameTypes,
    helpFolders,
    sourcesModule,
    currenciesForSelect,
    operatorsForSelect,
    softSwissGamesForSelect,
    engineOperators,
    providerGames,
    tableStatuses
  }
})
